import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import * as moment from 'moment-timezone';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { Theme } from '../../layouts/ats-service/service-models/theme';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import * as ClassicEditor from '../../vendors/ckeditor5/ckeditor';
import { ApplicantFileService } from '../applicant-file-service/applicant-file.service';
import { InterviewModel } from '../applicant-file-service/models/interview-model';
import { InterviewSelectionModel } from '../applicant-file-service/models/interview-selection-model';
import { Message } from '../applicant-file-service/models/message';
import { TimeSlot } from '../applicant-file-service/service-models/time-slot';
import { JobStatusModel } from '../job-service/models/job-status-model';
import { JobStatusService } from '../job-status.service';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent implements OnInit, OnDestroy, AfterViewInit {
  public Editor = ClassicEditor;
  identifier: string;
  theme: Theme;
  private subBrandIdentifier: string;
  private subscription = new Subscription();

  applicantFileId: number;
  availableTimeSlots: Array<TimeSlot>;
  hasSubmitted: boolean;
  interviewId: number;
  jobId: number;
  model: InterviewModel;
  saveModel: InterviewSelectionModel;
  submittedPrior: boolean;
  @ViewChild('thankYouModal')
  thankYouModal: ModalDirective;
  eventCancelled: boolean;
  rejectedInterview: boolean;
  showRejectedInterviewOption: boolean;

  editorConfig: {} = {
    toolbar: ['bold', 'italic', 'underline', '|', 'numberedList', 'bulletedList', '|', 'link']
  };
  supportedFileTypes = [
    '.doc',
    '.docx',
    '.pdf',
    '.rtf',
    '.txt',
    '.xls',
    '.xlsx',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.tif'];
  maxFileSize = 5242880;
  maxCumulativeFileSize = 5242880 * 4;
  saveAttachmentRoute: string;

  abbrs = {
    EST: 'Eastern Standard Time',
    EDT: 'Eastern Daylight Time',
    CST: 'Central Standard Time',
    CDT: 'Central Daylight Time',
    MST: 'Mountain Standard Time',
    MDT: 'Mountain Daylight Time',
    PST: 'Pacific Standard Time',
    PDT: 'Pacific Daylight Time',
  };
    processingServerSideRequest: boolean;
  
  constructor(private route: ActivatedRoute,
    private applicantFileService: ApplicantFileService,
    private jobStatusService: JobStatusService,
    private router: Router,
    private toast: ToastService) {
    this.saveModel = new InterviewSelectionModel();
  }

  canSubmit() {
    return (this.saveModel.timeSlotId > 0 || this.rejectedInterview) && !this.hasSubmitted;
  }

  rejectedInterviewChanged() {
    this.rejectedInterview = true;
  }

  confirmTimeSlot(timeSlot: TimeSlot) {
    this.saveModel.timeSlotId = timeSlot.id;
    this.rejectedInterview = false;
  }

  ngAfterViewInit(): void {
    if (this.submittedPrior && !this.eventCancelled) {
      this.hasSubmitted = true;
      this.thankYouModal.show();
    }
  }
  
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    moment.tz.link([
      'Pacific/Honolulu|Hawaiian Standard Time',
      'America/Anchorage|Alaskan Standard Time',
      'America/Los_Angeles|Pacific Standard Time',
      'America/Denver|Mountain Standard Time',
      'America/Chicago|Central Standard Time',
      'America/New_York|Eastern Standard Time'
    ]);

    const context = this;

    this.subscription.add(this.route.data
      .subscribe((data: { model: Theme }) => {
        this.theme = Object.assign(new Theme(), data.model);
      })
    );

    moment.fn.zoneName = function () {
      const abbr = this.zoneAbbr();
      return context.abbrs[abbr] || abbr;
    };
    this.subscription.add(
      this.route.parent.params.subscribe((params: Params) => {
        this.identifier = params['identifier'];
        this.subBrandIdentifier = params['subBrandIdentifier'];
        this.applicantFileId = +params['applicantFileId'];
        this.jobId = +params['jobId'];
        this.saveAttachmentRoute = `jobs/${this.jobId}/applicant-files/${this.applicantFileId}/messages/attachments`;
    }));

    this.subscription.add(this.route.params.subscribe((params: Params) => {
      this.interviewId = +params['interviewId'];
    }));
    this.subscription.add(this.route.data
      .subscribe((data: { model: InterviewModel, status: JobStatusModel }) => {
        this.model = data.model;
        this.jobStatusService.checkIfPageAccessible(data.status, false);
        this.saveModel.comments = this.model.interview.comments;

        const message = this.model.messages[0];
        this.saveModel.message = Object.assign(
          new Message(),
          {
            type: message.type,
            fromAddress: message.toAddress,
            fromName: message.toName,
            threadId: message.threadId,
            source: message.source
          });

        this.submittedPrior = this.model.interview.offeredTimeSlots.some(x => x.isConfirmed && !x.isCancelled);
        this.availableTimeSlots = this.model.interview.offeredTimeSlots.filter(x => x.isAvailable);
        this.eventCancelled = this.model.interview.offeredTimeSlots.every(x => x.isCancelled);
      }));
  }

  save() {
    this.saveModel.message.body = this.saveModel.message.content;
    this.saveModel.comments = this.saveModel.message.content;
    this.saveModel.applicantTimeZone = moment.tz.guess();

    this.processingServerSideRequest = true;
    if (this.rejectedInterview) {
      this.subscription.add(
        this.applicantFileService.rejectInterview(this.jobId, this.applicantFileId, this.interviewId, this.saveModel.message)
          .subscribe(data => {
            this.processingServerSideRequest = false;
            this.showThankYouPage();
          }));
    } else {
      this.subscription.add(this.applicantFileService.confirmInterview(this.jobId, this.applicantFileId, this.interviewId, this.saveModel)
        .subscribe(
          data => {
            this.processingServerSideRequest = false;
            this.showThankYouPage();
          },
          error => {
            this.processingServerSideRequest = false;
            if (error.status === 400 && error.error.TimeSlotUnavailable) {
              this.toast.error('This time is no longer available. Please select a different time.');
            }
          }));
    }
  }

  showThankYouPage() {
    this.hasSubmitted = true;
    this.router.navigate(nullSafePath(
      this.identifier,
      this.subBrandIdentifier,
      'jobs', this.jobId,
      'applicants', this.applicantFileId,
      'schedule', this.interviewId, 'thank-you'));
  }

  getTimeZone(date: Date) {
    return moment.tz(date, moment.tz.guess()).format('zz');
  }
}
